import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Typography,
  Popover,
  CircularProgress,
  Menu,
  MenuItem,
  Theme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { useLocation } from "react-router-dom";
import { CloudUpload } from "@mui/icons-material";
import { useRecoilValue } from "recoil";
import { OrganizationInterface } from "../../types";
import { organizationSelector } from "../../selectorsAtoms";
import { useUploadFiles } from "../../mutationsQueries/vouchers";
import isEqual from "react-fast-compare";
// @ts-ignore
import { usePrevious } from "react-hookedup";
import { ORGANIZATIONS_PAGE } from "../../util/constants";

const useStyles = makeStyles((theme: Theme) => ({
  input: {
    display: "none",
  },
  typography: {
    padding: `${theme.spacing(1)} ${theme.spacing(1.5)}`,
  },
  popover: {
    marginTop: theme.spacing(0.5),
  },
  disabledButton: {
    backgroundColor: `${theme.palette.secondary.main} !important`,
  },
}));

type MenuPropTypes = {
  organizations: OrganizationInterface[];
  anchorEl: null | HTMLButtonElement;
  onClose(): void;
  onSelectOrganization(value: OrganizationInterface): void;
};

const OrganizationsMenu = memo(
  ({
    organizations = [],
    anchorEl,
    onClose,
    onSelectOrganization,
  }: MenuPropTypes) => {
    return (
      <Menu open={!!anchorEl} anchorEl={anchorEl} onClose={onClose}>
        <MenuItem disabled divider dense>
          Select Organization
        </MenuItem>
        {organizations?.map((item, index: number) => (
          <MenuItem
            key={item.id}
            dense
            onClick={() => onSelectOrganization(item)}
          >
            {item.name}
          </MenuItem>
        ))}
      </Menu>
    );
  },
  isEqual
);

const FileUpload = () => {
  const [files, setFiles] = useState<FileList | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const [anchorElOrganizationsMenu, setAnchorElOrganizationsMenu] =
    useState<null | HTMLButtonElement>(null);
  const organizations =
    useRecoilValue<OrganizationInterface[]>(organizationSelector);
  const [selectedOneOrganization, setSelectedOneOrganization] =
    useState<OrganizationInterface>();
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement>(null);
  const prevMenuOpen = usePrevious(!!anchorElOrganizationsMenu);
  const location = useLocation();
  const classes = useStyles();

  useEffect(() => {
    if (selectedOneOrganization && !anchorElOrganizationsMenu && prevMenuOpen) {
      uploadButtonRef.current?.click();
    }
  }, [
    selectedOneOrganization,
    uploadButtonRef,
    anchorElOrganizationsMenu,
    prevMenuOpen,
  ]);

  useEffect(() => {
    if (organizations?.length === 1) {
      setSelectedOneOrganization(organizations[0]);
    } else {
      setSelectedOneOrganization(undefined);
    }
  }, [organizations]);

  const resetFields = useCallback(() => {
    setFiles(null);
    inputRef!.current!.value! = "";
    if (organizations?.length > 1) {
      setSelectedOneOrganization(undefined);
    }
  }, [inputRef, organizations]);

  const { isLoading } = useUploadFiles(
    selectedOneOrganization?.id,
    files,
    resetFields
  );

  const onImageUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setFiles(event.target.files);
    },
    []
  );

  const onButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (!selectedOneOrganization) {
        const hasOrganizations =
          Array.isArray(organizations) && organizations.length > 0;
        if (!hasOrganizations) {
          e.preventDefault();
          setAnchorEl(e.currentTarget);
        } else if (organizations.length > 1) {
          e.preventDefault();
          setAnchorElOrganizationsMenu(e.currentTarget);
        }
      }
    },
    [organizations, selectedOneOrganization]
  );

  const onCloseOrganizationsMenu = useCallback(
    () => setAnchorElOrganizationsMenu(null),
    []
  );

  const onSelectOrganization = useCallback(
    (organization: OrganizationInterface) => {
      setSelectedOneOrganization(organization);
      onCloseOrganizationsMenu();
    },
    [onCloseOrganizationsMenu]
  );

  return (
    <>
      <input
        ref={inputRef}
        accept=".png,.jpg,.jpeg,.pdf,.tiff"
        className={classes.input}
        id="contained-button-file"
        multiple
        type="file"
        disabled={isLoading || location.pathname.startsWith(ORGANIZATIONS_PAGE)}
        onChange={onImageUpload}
      />
      <label htmlFor="contained-button-file">
        <Button
          ref={uploadButtonRef}
          variant="contained"
          disableElevation
          color={"secondary"}
          component="span"
          classes={{
            disabled: classes.disabledButton,
          }}
          disabled={
            isLoading || location.pathname.startsWith(ORGANIZATIONS_PAGE)
          }
          onClick={onButtonClick}
          startIcon={
            isLoading ? (
              <CircularProgress size={15} color={"primary"} />
            ) : (
              <CloudUpload />
            )
          }
        >
          Upload file
        </Button>
      </label>
      <Popover
        id={"upload-file-popover"}
        className={classes.popover}
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
      >
        <Typography className={classes.typography}>
          Please select the organization
        </Typography>
      </Popover>
      <OrganizationsMenu
        organizations={organizations}
        anchorEl={anchorElOrganizationsMenu}
        onClose={onCloseOrganizationsMenu}
        onSelectOrganization={onSelectOrganization}
      />
    </>
  );
};

export default memo(FileUpload);
